import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface BootScreenProps {
  onComplete: () => void;
}

export function BootScreen({ onComplete }: BootScreenProps) {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<'logo' | 'loading' | 'complete'>('logo');
  const [loadingText, setLoadingText] = useState('Initializing system...');

  const loadingMessages = [
    'Initializing system...',
    'Loading components...',
    'Preparing workspace...',
    'Starting services...',
    'Almost ready...',
    'Welcome!'
  ];

  useEffect(() => {
    // Logo phase - show for 1 second
    const logoTimer = setTimeout(() => {
      setPhase('loading');
    }, 1000);

    return () => clearTimeout(logoTimer);
  }, []);

  useEffect(() => {
    if (phase !== 'loading') return;

    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        // Random increment for realistic feel
        const increment = Math.random() * 15 + 5;
        return Math.min(prev + increment, 100);
      });
    }, 200);

    return () => clearInterval(progressInterval);
  }, [phase]);

  useEffect(() => {
    // Update loading text based on progress
    const messageIndex = Math.min(
      Math.floor(progress / (100 / loadingMessages.length)),
      loadingMessages.length - 1
    );
    setLoadingText(loadingMessages[messageIndex]);

    if (progress >= 100) {
      setPhase('complete');
      const completeTimer = setTimeout(() => {
        onComplete();
      }, 500);
      return () => clearTimeout(completeTimer);
    }
  }, [progress, onComplete]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-99999 flex flex-col items-center justify-center bg-[#0a0a0f]"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Animated background gradient */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute inset-0 opacity-30"
            style={{
              background: 'radial-gradient(circle at 50% 50%, #1a1a2e 0%, transparent 50%)',
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>

        {/* Logo */}
        <motion.div
          className="relative z-10 flex flex-col items-center"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {/* Main Logo Icon */}
          <motion.div
            className="relative mb-8"
            animate={phase === 'loading' ? { 
              rotateY: [0, 360],
            } : {}}
            transition={{ 
              duration: 3, 
              repeat: Infinity, 
              ease: 'linear' 
            }}
          >
            <div className="w-24 h-24 relative">
              {/* Outer ring */}
              <motion.div
                className="absolute inset-0 rounded-2xl border-2 border-blue-500/50"
                animate={{
                  boxShadow: [
                    '0 0 20px rgba(59, 130, 246, 0.3)',
                    '0 0 40px rgba(59, 130, 246, 0.5)',
                    '0 0 20px rgba(59, 130, 246, 0.3)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              
              {/* Inner content */}
              <div className="absolute inset-2 rounded-xl bg-bg-blue-600 flex items-center justify-center">
                <motion.span 
                  className="text-4xl font-bold text-white"
                  animate={{ opacity: [1, 0.7, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  LA
                </motion.span>
              </div>

              {/* Orbiting dots */}
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 rounded-full bg-blue-400"
                  style={{
                    top: '50%',
                    left: '50%',
                    marginTop: -4,
                    marginLeft: -4,
                  }}
                  animate={{
                    x: [0, 50 * Math.cos((i * 2 * Math.PI) / 3 + progress * 0.1), 0],
                    y: [0, 50 * Math.sin((i * 2 * Math.PI) / 3 + progress * 0.1), 0],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.3,
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* Name */}
          <motion.h1
            className="text-3xl font-bold text-white mb-2 tracking-wider"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Lukman Afandi
          </motion.h1>

          <motion.p
            className="text-gray-400 text-sm mb-12"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Personal OS v1.0
          </motion.p>

          {/* Progress Section */}
          <AnimatePresence mode="wait">
            {phase !== 'logo' && (
              <motion.div
                className="flex flex-col items-center gap-4 w-80"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Progress bar container */}
                <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-linear-to-r from-blue-500 to-purple-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ ease: 'easeOut' }}
                  />
                </div>

                {/* Loading text */}
                <motion.p
                  key={loadingText}
                  className="text-gray-400 text-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  {loadingText}
                </motion.p>

                {/* Progress percentage */}
                <p className="text-gray-500 text-xs font-mono">
                  {Math.round(progress)}%
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Bottom decoration */}
        <motion.div
          className="absolute bottom-8 flex gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full bg-gray-600"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
